import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Users, Award, Clock, Shield, Zap, Code } from 'lucide-react';
import { services } from '../data/services';
import ServiceCard from './ServiceCard';
import ProjectCarousel from './ProjectCarousel';
import ContactForm from './ContactForm';
import FAQSection from './FAQSection';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Elements */}
        <img src="/bg.webp" alt="Background" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
        {/* Animated background shapes */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-400/5 to-blue-600/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-4xl mx-auto">
            {/* Main Headline */}
            <motion.h1
              className="mt-10 sm:mt-0 mb-6 text-4xl sm:text-5xl font-bold text-blue-400"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Transform Your
              <span className="inline-block text-white ml-6">
                Digital Future
              </span>
            </motion.h1>

            <motion.h2
              className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              HARMITON WebTech delivers cutting-edge IT and business solutions that drive growth,
              innovation, and success in today's digital landscape.
            </motion.h2>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mx-auto w-fit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <a
                href="#services"
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 group transform hover:scale-105 hover:shadow-xl"
              >
                <span>Explore Our Services</span>
              </a>
              <a
                href="#contact"
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 group transform hover:scale-105 hover:shadow-xl"
              >
                Get A Quote
              </a>
            </motion.div>

            {/* Key Features */}
            <motion.div
              className="grid md:grid-cols-3 gap-8 mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, staggerChildren: 0.2 }}
            >
              <motion.div
                className="flex flex-col items-center text-center group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  whileHover={{ scale: 1.1, rotate: 3 }}
                >
                  <Code className="w-8 h-8" />
                </motion.div>
                <h3 className="text-xl text-blue-600 font-semibold mb-2">Full-Stack Development</h3>
                <p className="text-white">From websites to mobile apps, we build complete solutions</p>
              </motion.div>

              <motion.div
                className="flex flex-col items-center text-center group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  whileHover={{ scale: 1.1, rotate: 3 }}
                >
                  <Zap className="w-8 h-8" />
                </motion.div>
                <h3 className="text-xl text-blue-600 font-semibold mb-2">Business Strategy</h3>
                <p className="text-white">Data-driven insights and consulting for growth</p>
              </motion.div>

              <motion.div
                className="flex flex-col items-center text-center group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  whileHover={{ scale: 1.1, rotate: 3 }}
                >
                  <Users className="w-8 h-8" />
                </motion.div>
                <h3 className="text-xl text-blue-600 font-semibold mb-2">Customer-First</h3>
                <p className="text-white mb-10 sm:mb-2">Tailored solutions focused on your success</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 relative">
              Our <span className="text-blue-600">Core Services</span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full"></div>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8">
              Comprehensive IT and business solutions tailored to drive your digital transformation
              and business growth.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, staggerChildren: 0.1 }}
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 relative">
              Featured <span className="text-blue-600">Projects</span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full"></div>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8">
              Showcasing our expertise through successful project implementations
              across various industries and technologies.
            </p>
          </motion.div>
          <ProjectCarousel />
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 relative">
              Why Choose <span className="text-blue-600">HARMITON WebTech</span>?
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full"></div>
            </h2>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, staggerChildren: 0.1 }}
            viewport={{ once: true }}
          >
            {[
              {
                title: 'Customer-First Approach',
                description: 'Solutions tailored specifically to your business goals and requirements.',
              },
              {
                title: 'Scalable Solutions',
                description: 'Affordable services designed for sustainable long-term growth.',
              },
              {
                title: 'Latest Technologies',
                description: 'Cutting-edge tech stack blending creativity with strategic thinking.',
              },
              {
                title: 'Transparent Support',
                description: 'Reliable, result-oriented support with complete transparency.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="text-center group hover:transform hover:scale-105 transition-all duration-300"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <motion.div
                  className="h-12 w-12 text-green-500 mx-auto mb-4"
                  whileHover={{ scale: 1.1 }}
                >
                  <CheckCircle />
                </motion.div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="flex justify-center mb-4"
              whileHover={{ scale: 1.1 }}
            >
              <Zap className="h-8 w-8 text-blue-600" />
            </motion.div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 relative">
              Ready to Start Your <span className="text-blue-600">Digital Journey</span>?
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full"></div>
            </h2>
            <p className="text-xl text-gray-600 mt-8">
              Let's discuss how we can help transform your business with our innovative solutions.
            </p>
          </motion.div>
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <ContactForm source="homepage" />
          </motion.div>
        </div>
      </section>
      <FAQSection />
    </div>
  );
};

export default HomePage;